import * as React from "react";
import {
  IconButton,
  FormControl,
  OutlinedInput,
  FormHelperText,
  Fade,
  Backdrop,
} from "@mui/material";
import { Formik } from "formik";
import Box from "@mui/material/Box";
import Loader from "components/loader";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import { useDispatch } from "react-redux";
import { useTheme } from "@mui/material/styles";
import { Close, DirectionsBike, DirectionsCar } from "@mui/icons-material";
import { VehicleDetails } from "utils/modals";
import { CarCompanySchema } from "utils/schema";
import Typography from "@mui/material/Typography";
import AnimateButton from "components/animateButton";
import { addNewVehicle, updateVehicle } from "modules/vehicle/action";
import CarTypeDropDown from "components/carTypeDropdown";
import CompanyDropDown from "components/companyDropdown";
import { BRAND } from "themes";

const VEHICLE_TYPE_OPTIONS = [
  { label: "Car", value: "0", icon: DirectionsCar },
  { label: "Bike", value: "1", icon: DirectionsBike },
];

const getVehicleId = (vehicle: any) =>
  vehicle?.vehicleId || vehicle?._id || vehicle?.id || "";

const getCompanyId = (vehicle: any) =>
  vehicle?.brandId ||
  vehicle?.companyId ||
  vehicle?.carCompany?._id ||
  vehicle?.carCompany?.id ||
  "";

const getCompanyName = (vehicle: any) =>
  vehicle?.brandName ||
  vehicle?.companyName ||
  vehicle?.carCompany?.name ||
  "";

const getVehicleType = (type: any) => (String(type) === "1" ? "1" : "0");

type SubmittedVehicle = {
  name: string;
  vehicleType: number;
  companyId: string;
  carTypeId?: string;
};

export default function AddNewVehicleModal({
  state,
  setState,
  selectedVehicle,
  defaultVehicleType,
  onSuccess,
}: any) {
  const theme = useTheme();
  const dispatch: any = useDispatch();
  const [loading, setLoading] = React.useState(false);
  const [selectedCarType, setSelectedCarType] = React.useState("");

  const isEdit = Boolean(selectedVehicle);
  const secondary = theme.palette.secondary.main || BRAND.secondary;
  const secondaryDark = theme.palette.secondary.dark || BRAND.primaryDark;
  const secondaryLight = theme.palette.secondary.light || "#ede7f6";
  const border = theme.palette.divider || BRAND.border;
  const text = theme.palette.text.primary || BRAND.ink;
  const muted = theme.palette.text.secondary || BRAND.muted;
  const surfaceMuted = theme.palette.grey[50] || BRAND.track;

  const modalSx = {
    root: {
      position: "absolute" as const,
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)",
      width: { xs: "92%", sm: 480 },
      maxHeight: "90vh",
      overflow: "auto",
      bgcolor: "background.paper",
      borderRadius: "20px",
      border: `1px solid ${border}`,
      boxShadow:
        "0 24px 48px rgba(27, 19, 64, 0.14), 0 8px 16px rgba(27, 19, 64, 0.06)",
      outline: "none",
    },
    header: {
      position: "relative" as const,
      px: 3,
      pt: 3,
      pb: 2.5,
      background: `linear-gradient(135deg, ${secondaryLight} 0%, #FFFFFF 58%, ${surfaceMuted} 100%)`,
      borderBottom: `1px solid ${border}`,
    },
    accentBar: {
      position: "absolute" as const,
      top: 0,
      left: 0,
      right: 0,
      height: 4,
      background: `linear-gradient(90deg, ${BRAND.primary} 0%, ${secondary} 100%)`,
      borderRadius: "20px 20px 0 0",
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.5,
      mb: 1.25,
      borderRadius: "999px",
      bgcolor: secondaryLight,
      color: secondaryDark,
      fontSize: 12,
      fontWeight: 600,
      letterSpacing: "0.02em",
    },
    title: {
      color: text,
      fontWeight: 700,
      fontSize: { xs: 22, sm: 24 },
      lineHeight: 1.25,
      letterSpacing: "-0.02em",
    },
    subtitle: {
      mt: 0.75,
      color: muted,
      fontSize: 14,
      lineHeight: 1.5,
    },
    closeBtn: {
      position: "absolute" as const,
      top: 14,
      right: 14,
      color: muted,
      bgcolor: "rgba(255,255,255,0.85)",
      border: `1px solid ${border}`,
      transition: "all 200ms ease",
      "&:hover": {
        color: text,
        bgcolor: surfaceMuted,
        borderColor: theme.palette.grey[300],
      },
    },
    body: {
      px: 3,
      py: 2.5,
      display: "flex",
      flexDirection: "column" as const,
      gap: 2.25,
    },
    fieldLabel: {
      mb: 0.75,
      color: text,
      fontSize: 13,
      fontWeight: 600,
    },
    typeToggle: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 1,
      p: 0.5,
      borderRadius: "14px",
      bgcolor: surfaceMuted,
      border: `1px solid ${border}`,
    },
    typeOption: (active: boolean) => ({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 1,
      py: 1.25,
      px: 1.5,
      borderRadius: "10px",
      border: "none",
      cursor: "pointer",
      fontSize: 14,
      fontWeight: 600,
      transition: "all 200ms ease",
      color: active ? secondaryDark : muted,
      bgcolor: active ? "#fff" : "transparent",
      boxShadow: active ? `0 2px 10px ${secondary}33` : "none",
      "&:hover": {
        color: secondaryDark,
        bgcolor: active ? "#fff" : "rgba(255,255,255,0.7)",
      },
    }),
    fieldShell: {
      width: "100%",
      "& .MuiOutlinedInput-root, & .MuiAutocomplete-root .MuiOutlinedInput-root":
        {
          borderRadius: "12px",
          bgcolor: "#fff",
          transition: "box-shadow 200ms ease",
          "& fieldset": {
            borderColor: border,
          },
          "&:hover fieldset": {
            borderColor: theme.palette.grey[400],
          },
          "&.Mui-focused fieldset": {
            borderColor: secondary,
            borderWidth: "1.5px",
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${secondary}22`,
          },
        },
    },
    footer: {
      px: 3,
      py: 2,
      display: "flex",
      justifyContent: "flex-end",
      alignItems: "center",
      gap: 1.5,
      borderTop: `1px solid ${border}`,
      bgcolor: surfaceMuted,
    },
    cancelBtn: {
      minWidth: 100,
      borderRadius: "12px",
      textTransform: "none" as const,
      fontWeight: 600,
      color: muted,
      borderColor: border,
      "&:hover": {
        borderColor: theme.palette.grey[400],
        bgcolor: "#fff",
        color: text,
      },
    },
    submitBtn: {
      minWidth: 150,
      borderRadius: "12px",
      textTransform: "none" as const,
      fontWeight: 700,
      boxShadow: `0 8px 20px ${secondary}40`,
      "&:hover": {
        boxShadow: `0 10px 24px ${secondary}55`,
      },
    },
  };

  React.useEffect(() => {
    if (selectedVehicle) {
      setSelectedCarType(selectedVehicle?.carTypeName || "");
    } else {
      setSelectedCarType("");
    }
  }, [selectedVehicle]);

  const handleClose = () => {
    setState(false);
  };

  const initialValues = isEdit
    ? {
        ...new VehicleDetails(),
        id: getVehicleId(selectedVehicle),
        name: selectedVehicle?.vehicleName || selectedVehicle?.name || "",
        vehicleType: getVehicleType(selectedVehicle?.vehicleType),
        companyId: getCompanyId(selectedVehicle),
        companyName: getCompanyName(selectedVehicle),
        carCompany: {
          id: getCompanyId(selectedVehicle),
          _id: getCompanyId(selectedVehicle),
          name: getCompanyName(selectedVehicle),
          logo: selectedVehicle?.logo || "",
        },
        carTypeId: selectedVehicle?.carTypeId || "",
        logo: selectedVehicle?.logo || "",
      }
    : {
        ...new VehicleDetails(),
        vehicleType: getVehicleType(defaultVehicleType ?? "0"),
      };

  const handleSubmit = (values: VehicleDetails) => {
    setLoading(true);

    const isBike = String(values.vehicleType) === "1";
    const payload: any = {
      name: values.name,
      vehicleType: Number(values.vehicleType),
      companyId:
        values.carCompany?.id ||
        values.carCompany?._id ||
        values.companyId ||
        "",
    };

    if (!isBike) {
      payload.carTypeId = values.carTypeId;
    }

    const submitted: SubmittedVehicle = {
      name: payload.name,
      vehicleType: payload.vehicleType,
      companyId: payload.companyId,
      ...(payload.carTypeId ? { carTypeId: payload.carTypeId } : {}),
    };

    const finish = (result?: any) => {
      setLoading(false);
      setState(false);
      onSuccess?.(result, submitted);
    };

    if (isEdit) {
      dispatch(
        updateVehicle({
          data: {
            id: getVehicleId(selectedVehicle),
            ...payload,
          },
          callback: finish,
        }),
      );
    } else {
      dispatch(
        addNewVehicle({
          data: payload,
          callback: finish,
        }),
      );
    }
  };

  return (
    <Modal
      open={state}
      onClose={handleClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 250,
        sx: {
          backgroundColor: "rgba(27, 19, 64, 0.45)",
          backdropFilter: "blur(4px)",
        },
      }}
      aria-labelledby="add-vehicle-modal-title"
      aria-describedby="add-vehicle-modal-description"
    >
      <Fade in={state}>
        <Box sx={modalSx.root}>
          <Box sx={modalSx.header}>
            <Box sx={modalSx.accentBar} />
            <Box sx={modalSx.badge}>
              {isEdit ? "Edit model" : "Vehicle catalog"}
            </Box>
            <Typography
              id="add-vehicle-modal-title"
              sx={modalSx.title}
              component="h2"
            >
              {isEdit ? "Update Vehicle" : "Add New Vehicle"}
            </Typography>
            <Typography
              id="add-vehicle-modal-description"
              sx={modalSx.subtitle}
            >
              {isEdit
                ? "Adjust the model details below. Changes apply across the catalog."
                : "Choose Car or Bike, then fill company and model details."}
            </Typography>
            <IconButton
              onClick={handleClose}
              sx={modalSx.closeBtn}
              aria-label="Close"
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={CarCompanySchema}
            onSubmit={handleSubmit}
          >
            {({
              errors,
              touched,
              values,
              handleBlur,
              setFieldValue,
              handleSubmit,
            }) => {
              const isBike = String(values.vehicleType) === "1";

              return (
                <form noValidate onSubmit={handleSubmit}>
                  <Box sx={modalSx.body}>
                    <Box>
                      <Typography sx={modalSx.fieldLabel}>
                        Vehicle Type
                      </Typography>
                      <Box
                        sx={modalSx.typeToggle}
                        role="radiogroup"
                        aria-label="Vehicle type"
                      >
                        {VEHICLE_TYPE_OPTIONS.map((option) => {
                          const active =
                            String(values.vehicleType) === option.value;
                          const Icon = option.icon;
                          return (
                            <Box
                              key={option.value}
                              component="button"
                              type="button"
                              role="radio"
                              aria-checked={active}
                              onClick={() => {
                                setFieldValue("vehicleType", option.value);
                                if (option.value === "1") {
                                  setFieldValue("carTypeId", "");
                                  setSelectedCarType("");
                                }
                              }}
                              sx={modalSx.typeOption(active)}
                            >
                              <Icon sx={{ fontSize: 20 }} />
                              {option.label}
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    {!isBike && (
                      <Box sx={modalSx.fieldShell}>
                        <Typography sx={modalSx.fieldLabel}>
                          Car Type
                        </Typography>
                        <FormControl fullWidth>
                          <CarTypeDropDown
                            labelProp="Select Car Type"
                            selectedCarType={selectedCarType}
                            onSelectCarType={(carType: any) => {
                              const carTypeName =
                                carType?.carType || carType?.name || "";
                              setSelectedCarType(carTypeName);
                              setFieldValue(
                                "carTypeId",
                                carType?.id || carType?._id || "",
                              );
                            }}
                          />
                        </FormControl>
                      </Box>
                    )}

                    <Box sx={modalSx.fieldShell}>
                      <Typography sx={modalSx.fieldLabel}>Company</Typography>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.carCompany && errors.carCompany)}
                      >
                        <CompanyDropDown
                          labelProp="Select Company"
                          selectedCompany={values.carCompany}
                          onSelectCompany={(company: any) => {
                            setFieldValue("carCompany", company);
                            setFieldValue(
                              "companyId",
                              company?.id || company?._id || "",
                            );
                            setFieldValue(
                              "companyName",
                              company?.name || company?.brandName || "",
                            );
                          }}
                        />
                        {touched.carCompany && errors.carCompany && (
                          <FormHelperText error>
                            {String(errors.carCompany)}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>

                    <Box sx={modalSx.fieldShell}>
                      <Typography sx={modalSx.fieldLabel}>
                        {isBike ? "Bike Name" : "Vehicle Name"}
                      </Typography>
                      <FormControl
                        fullWidth
                        error={Boolean(touched.name && errors.name)}
                      >
                        <OutlinedInput
                          fullWidth
                          required
                          type="text"
                          name="name"
                          onBlur={handleBlur}
                          onChange={(event) =>
                            setFieldValue("name", event.target.value)
                          }
                          value={values.name}
                          placeholder={
                            isBike
                              ? "e.g. Activa 6G"
                              : "e.g. Creta, City, Swift"
                          }
                          inputProps={{ maxLength: 15 }}
                        />
                        {touched.name && errors.name && (
                          <FormHelperText error>
                            {String(errors.name)}
                          </FormHelperText>
                        )}
                      </FormControl>
                    </Box>
                  </Box>

                  <Box sx={modalSx.footer}>
                    <Button
                      variant="outlined"
                      onClick={handleClose}
                      sx={modalSx.cancelBtn}
                    >
                      Cancel
                    </Button>
                    <AnimateButton>
                      <Button
                        disableElevation
                        size="large"
                        type="submit"
                        variant="contained"
                        color="secondary"
                        sx={modalSx.submitBtn}
                      >
                        {isEdit ? "Save Changes" : "Add Vehicle"}
                      </Button>
                    </AnimateButton>
                  </Box>
                </form>
              );
            }}
          </Formik>

          {loading && <Loader />}
        </Box>
      </Fade>
    </Modal>
  );
}
